import { useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { LanguageMenu } from '../components/layout/LanguageMenu/index.js';
import { useAuth } from '../hooks/useAuth.js';

/**
 * @graph-node {Page}
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business ログインページ。Google OAuth認証ボタン・言語切替・認証済みリダイレクトを提供
 * @graph-connects useAuth [calls] 認証フック呼び出し
 * @graph-connects LanguageMenu [calls] ja / en / vi の言語切替メニュー
 */
export const Route = createFileRoute('/login')({
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
});

/** @graph-connects none */
function LoginPage() {
  const { t } = useTranslation();
  const { login, isLoggingIn, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { redirect } = Route.useSearch();

  // 認証済みならリダイレクト
  useEffect(() => {
    if (isAuthenticated) {
      void navigate({ to: redirect ?? '/pipelines' });
    }
  }, [isAuthenticated, redirect, navigate]);

  const handleGoogleLogin = () => {
    if (redirect) {
      sessionStorage.setItem('auth_redirect', redirect);
    }
    login();
  };

  return (
    <div className="min-h-screen bg-[var(--color-bg-base)] flex items-center justify-center p-4">
      {/* 背景装飾 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="gradient-orb gradient-orb-primary w-96 h-96 -top-48 -left-48" />
        <div className="gradient-orb gradient-orb-success w-96 h-96 -bottom-48 -right-48" />
      </div>

      <div className="relative w-full max-w-md">
        {/* 言語切替 */}
        <div className="absolute -top-12 right-0">
          <LanguageMenu />
        </div>

        <div className="glass-card p-8 animate-fade-in-up">
          {/* ブランドロゴ */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold gradient-text">{t('app.name')}</h1>
            <p className="mt-2 text-sm text-[var(--color-text-muted)]">{t('app.subtitle')}</p>
          </div>

          {/* ログインボタン */}
          <button
            onClick={handleGoogleLogin}
            disabled={isLoggingIn}
            className="w-full flex items-center justify-center gap-3 px-6 py-3 rounded-lg font-medium text-white bg-[var(--color-accent-primary)] hover:bg-[var(--color-accent-hover)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors glow-accent"
            aria-label={t('auth.loginWith')}
          >
            {isLoggingIn ? (
              <div className="loading-spinner loading-spinner-sm border-white/30 border-t-white" />
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
            )}
            {t('auth.loginWith')}
          </button>

          {/* フッター */}
          <p className="mt-8 text-xs text-[var(--color-text-muted)] text-center">
            air-closet.com {t('auth.login')}
          </p>
        </div>
      </div>
    </div>
  );
}
